import React, { useState } from 'react';
import { User } from '../types';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (tier: 'pro' | 'premium') => void;
  user: User | null;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, onUpgrade, user }) => {
  const [step, setStep] = useState<'PLANS' | 'CHECKOUT'>('PLANS');
  const [selectedTier, setSelectedTier] = useState<'pro' | 'premium' | null>(null);
  
  // Checkout State
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'upi'>('card');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [upiId, setUpiId] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSelectPlan = (tier: 'pro' | 'premium') => {
    setSelectedTier(tier);
    setStep('CHECKOUT');
  };

  const handlePay = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTier) return;

    setProcessing(true);
    // Simulate Payment Gateway processing time
    setTimeout(() => {
      setProcessing(false);
      onUpgrade(selectedTier);
      alert(`Payment Successful! Welcome to ${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)}.`);
      // Reset State
      setStep('PLANS');
      setSelectedTier(null);
      setCardNumber('');
      onClose();
    }, 2500);
  };

  const renderPlans = () => (
    <>
      <div className="text-center p-8 pb-4">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Choose Your Plan</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2">Unlock your potential with ThrustED Premium</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-8 pt-0">
        {/* Free Plan */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6 flex flex-col">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">Free</h3>
          <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">₹0 <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/forever</span></div>
          <ul className="mt-6 space-y-3 flex-1 text-sm text-slate-600 dark:text-slate-350">
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Daily Current Affairs</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Limited Mock Tests (2-3)</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Previous Year Questions</li>
            <li className="flex items-center text-slate-400 dark:text-slate-550"><i className="fas fa-times mr-2"></i> Full Mock Series</li>
            <li className="flex items-center text-slate-400 dark:text-slate-550"><i className="fas fa-times mr-2"></i> Custom Test Generator</li>
          </ul>
          <button className="mt-8 w-full py-2 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-lg font-medium cursor-default">
            Current Plan
          </button>
        </div>

        {/* Pro Plan */}
        <div className="border border-orange-200 dark:border-orange-900/40 bg-orange-50/30 dark:bg-orange-950/10 rounded-xl p-6 flex flex-col relative transform scale-105 shadow-xl">
          <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">POPULAR</div>
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">Pro</h3>
          <div className="mt-2 text-3xl font-bold text-orange-600 dark:text-orange-450">₹1,500 <span className="text-sm font-normal text-slate-500 dark:text-slate-450">/year</span></div>
          <ul className="mt-6 space-y-3 flex-1 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> All Free Features</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> <strong>All Mock Tests</strong></li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Subject-wise Tests</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Detailed Analytics</li>
            <li className="flex items-center text-slate-400 dark:text-slate-550"><i className="fas fa-times mr-2"></i> Custom Test Generator</li>
          </ul>
          <button 
            onClick={() => handleSelectPlan('pro')}
            className="mt-8 w-full py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold shadow-lg shadow-orange-200 dark:shadow-none transition-all"
          >
            Upgrade to Pro
          </button>
        </div>

        {/* Premium Plan */}
        <div className="border border-purple-200 dark:border-purple-900/40 bg-purple-50/30 dark:bg-purple-950/10 rounded-xl p-6 flex flex-col">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">Premium</h3>
          <div className="mt-2 text-3xl font-bold text-purple-600 dark:text-purple-400">₹2,000 <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/year</span></div>
          <ul className="mt-6 space-y-3 flex-1 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Everything in Pro</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> <strong>Custom Test Generator</strong></li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> Smart No-Repeat Pool</li>
            <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i> AI-Powered Study Plans</li>
          </ul>
          <button 
            onClick={() => handleSelectPlan('premium')}
            className="mt-8 w-full py-2 bg-slate-900 hover:bg-slate-800 dark:bg-purple-600 dark:hover:bg-purple-700 text-white rounded-lg font-bold transition-all"
          >
            Get Premium
          </button>
        </div>
      </div>
      <div className="text-center p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-750 text-xs text-slate-500 dark:text-slate-400">
        100% Secure Checkout. 7-Day Money Back Guarantee.
      </div>
    </>
  );

  const renderCheckout = () => {
    const price = selectedTier === 'pro' ? '1,500' : '2,000';
    
    return (
      <div className="flex flex-col md:flex-row h-[600px] md:h-[500px]">
        {/* Order Summary Sidebar */}
        <div className="w-full md:w-1/3 bg-slate-50 dark:bg-slate-900 p-6 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700 flex flex-col">
          <button onClick={() => setStep('PLANS')} className="text-slate-500 hover:text-slate-800 dark:hover:text-white text-sm mb-6 flex items-center">
             <i className="fas fa-arrow-left mr-2"></i> Back to Plans
          </button>
          <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-4">Order Summary</h3>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mb-4">
             <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-slate-700 dark:text-slate-200 capitalize">{selectedTier} Plan</span>
                <span className="font-bold text-slate-900 dark:text-white">₹{price}</span>
             </div>
             <div className="text-xs text-slate-500 dark:text-slate-400">Annual Subscription</div>
          </div>
          
          <div className="flex justify-between items-center py-2 border-t border-slate-200 dark:border-slate-700 mt-auto">
             <span className="text-slate-600 dark:text-slate-350">Subtotal</span>
             <span className="text-slate-800 dark:text-slate-200">₹{price}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-200 dark:border-slate-700 mb-2">
             <span className="text-slate-600 dark:text-slate-350">Tax (18% GST)</span>
             <span className="text-slate-800 dark:text-slate-200">₹{(parseInt(price.replace(',', '')) * 0.18).toFixed(0)}</span>
          </div>
          <div className="flex justify-between items-center text-lg font-bold">
             <span className="text-slate-900 dark:text-white">Total</span>
             <span className="text-orange-600 dark:text-orange-400">₹{(parseInt(price.replace(',', '')) * 1.18).toFixed(0)}</span>
          </div>
        </div>

        {/* Payment Form */}
        <div className="w-full md:w-2/3 p-6 bg-white dark:bg-slate-800 overflow-y-auto">
           <h3 className="font-bold text-slate-800 dark:text-white text-xl mb-6">Secure Payment</h3>
           
           <div className="flex space-x-4 mb-6">
              <button 
                onClick={() => setPaymentMethod('card')}
                className={`flex-1 py-3 border rounded-lg flex flex-col items-center justify-center transition-all ${paymentMethod === 'card' ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'}`}
              >
                 <i className="far fa-credit-card text-xl mb-2"></i>
                 <span className="text-sm font-medium">Card</span>
              </button>
              <button 
                onClick={() => setPaymentMethod('upi')}
                className={`flex-1 py-3 border rounded-lg flex flex-col items-center justify-center transition-all ${paymentMethod === 'upi' ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'}`}
              >
                 <i className="fas fa-mobile-alt text-xl mb-2"></i>
                 <span className="text-sm font-medium">UPI / QR</span>
              </button>
           </div>

           <form onSubmit={handlePay} className="space-y-4">
              {paymentMethod === 'card' ? (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Card Number</label>
                    <div className="relative">
                      <i className="far fa-credit-card absolute left-3 top-3 text-slate-400"></i>
                      <input 
                        type="text" 
                        value={cardNumber}
                        onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').substring(0, 16))}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="0000 0000 0000 0000"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Expiry Date</label>
                      <input 
                        type="text" 
                        value={expiry}
                        onChange={e => setExpiry(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="MM / YY"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">CVV</label>
                      <input 
                        type="password" 
                        value={cvv}
                        onChange={e => setCvv(e.target.value.substring(0, 3))}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="123"
                        required
                      />
                    </div>
                  </div>
                  <div>
                     <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Card Holder Name</label>
                     <input 
                        type="text" 
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="Name on Card"
                        defaultValue={user?.name || ''}
                        required
                     />
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                   <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded text-center border border-slate-200 dark:border-slate-700">
                      <div className="w-32 h-32 bg-white dark:bg-slate-800 mx-auto border border-slate-300 dark:border-slate-700 flex items-center justify-center mb-2">
                         <i className="fas fa-qrcode text-6xl text-slate-800 dark:text-slate-200"></i>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Scan QR to Pay</p>
                   </div>
                   <div>
                     <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Or Enter UPI ID</label>
                     <input 
                        type="text" 
                        value={upiId}
                        onChange={e => setUpiId(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="username@upi"
                        required
                     />
                   </div>
                </div>
              )}

              <button 
                type="submit"
                disabled={processing}
                className="w-full mt-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold shadow-lg transition-all flex items-center justify-center"
              >
                {processing ? (
                   <><i className="fas fa-circle-notch fa-spin mr-2"></i> Processing Payment...</>
                ) : (
                   <><i className="fas fa-lock mr-2"></i> Pay ₹{(parseInt(price.replace(',', '')) * 1.18).toFixed(0)}</>
                )}
              </button>
              
              <div className="flex items-center justify-center gap-2 mt-4 opacity-60">
                 <i className="fab fa-cc-visa text-2xl text-slate-600 dark:text-slate-400"></i>
                 <i className="fab fa-cc-mastercard text-2xl text-slate-600 dark:text-slate-400"></i>
                 <i className="fab fa-cc-amex text-2xl text-slate-600 dark:text-slate-400"></i>
                 <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">128-bit SSL Encrypted</span>
              </div>
            </form>
         </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl relative overflow-hidden my-8 border border-transparent dark:border-slate-700">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors z-10"
        >
          <i className="fas fa-times"></i>
        </button>

        {step === 'PLANS' ? renderPlans() : renderCheckout()}
      </div>
    </div>
  );
};

export default PricingModal;